import { useState, useMemo, useCallback } from 'react';

/**
 * Hook to collapse columns of a partial (between previous separator and current one).
 * Click on a separator toggles its collapsed state. State persists in localStorage.
 *
 * @param {string} storageKey - unique key e.g. `collapsed-attendance-{classId}`
 * @param {Array} columns - array of column objects with .id and optional .isSeparator
 * @returns { hiddenColIds: Set, isCollapsed: (sepId)=>bool, toggle: (sepId)=>void }
 */
export function useCollapsedSeparators(storageKey, columns) {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      return new Set(raw ? JSON.parse(raw) : []);
    } catch {
      return new Set();
    }
  });

  const toggle = useCallback((sepId) => {
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(sepId)) next.delete(sepId);
      else next.add(sepId);
      try { localStorage.setItem(storageKey, JSON.stringify([...next])); } catch {}
      return next;
    });
  }, [storageKey]);

  const isCollapsed = useCallback((sepId) => collapsed.has(sepId), [collapsed]);

  // Compute hidden col IDs: for each collapsed separator, hide cols between previous separator (or start) and this one
  const hiddenColIds = useMemo(() => {
    const hidden = new Set();
    if (!Array.isArray(columns)) return hidden;
    let prevSepIdx = -1;
    for (let i = 0; i < columns.length; i++) {
      const col = columns[i];
      if (col?.isSeparator) {
        if (collapsed.has(col.id)) {
          for (let j = prevSepIdx + 1; j < i; j++) {
            hidden.add(columns[j].id);
          }
        }
        prevSepIdx = i;
      }
    }
    return hidden;
  }, [columns, collapsed]);

  return { hiddenColIds, isCollapsed, toggle };
}
